import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Alert, InputGroup, Badge, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { authService, settingsService } from '../../services/api';

const KEY_REGEX = /^[a-zA-Z0-9_.-]+$/;
const MAX_KEY_LENGTH = 128;
const MAX_VALUE_LENGTH = 2048;
const MAX_DESC_LENGTH = 512;
const TRUNCATE_THRESHOLD = 60;

const AppSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Creation state
  const [isCreating, setIsCreating] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newKeyError, setNewKeyError] = useState('');

  // Editing state
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Expansion state for long values: Set of keys that are expanded
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  // General saving state
  const [saving, setSaving] = useState(false);

  const currentUser = authService.getCurrentUser();
  const isCurrentUserAdmin = currentUser?.roles?.includes('admin');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const result = await settingsService.getSettings();
      if (result.success && Array.isArray(result.data)) {
        setSettings(result.data);
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: result.message || 'Errore nel caricamento delle impostazioni'
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore di connessione durante il caricamento delle impostazioni'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isCurrentUserAdmin) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [isCurrentUserAdmin, loadSettings]);

  // Filtered settings
  const filteredSettings = useMemo(() => {
    if (!searchTerm.trim()) return settings;
    const term = searchTerm.trim().toLowerCase();
    return settings.filter(
      (s) =>
        s.key.toLowerCase().includes(term) ||
        (s.description && s.description.toLowerCase().includes(term)) ||
        (s.value && s.value.toLowerCase().includes(term))
    );
  }, [settings, searchTerm]);

  // Toggle value expansion
  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Creation handlers
  const handleOpenNewRow = () => {
    // If editing another row, cancel it
    setEditingKey(null);
    setIsCreating(true);
    setNewKey('');
    setNewValue('');
    setNewDescription('');
    setNewKeyError('');
    setAlert({ show: false, type: '', message: '' });
  };

  const handleCreateCancel = () => {
    setIsCreating(false);
    setNewKey('');
    setNewValue('');
    setNewDescription('');
    setNewKeyError('');
  };

  const validateKey = (key) => {
    const trimmed = key.trim();
    if (!trimmed) {
      return 'La chiave è obbligatoria.';
    }
    if (trimmed.length > MAX_KEY_LENGTH) {
      return `La chiave non può superare i ${MAX_KEY_LENGTH} caratteri.`;
    }
    if (!KEY_REGEX.test(trimmed)) {
      return 'La chiave può contenere solo lettere, numeri, punti, trattini e underscore (nessuno spazio).';
    }
    if (settings.some((s) => s.key.toLowerCase() === trimmed.toLowerCase())) {
      return `Un'impostazione con la chiave '${trimmed}' esiste già.`;
    }
    return '';
  };

  const handleCreateSave = async () => {
    const trimmedKey = newKey.trim();
    const errorMsg = validateKey(trimmedKey);
    if (errorMsg) {
      setNewKeyError(errorMsg);
      return;
    }
    setNewKeyError('');

    setSaving(true);
    try {
      const result = await settingsService.createSetting(
        trimmedKey,
        newValue,
        newDescription.trim() || null
      );

      if (result.success) {
        setAlert({
          show: true,
          type: 'success',
          message: `Parametro '${trimmedKey}' creato con successo.`
        });
        setIsCreating(false);
        setNewKey('');
        setNewValue('');
        setNewDescription('');
        await loadSettings();
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: result.message || 'Errore nella creazione del parametro.'
        });
      }
    } catch (err) {
      console.error('Error creating setting:', err);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore di connessione durante la creazione.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Editing handlers
  const handleStartEdit = (setting) => {
    setIsCreating(false);
    setEditingKey(setting.key);
    setEditValue(setting.value ?? '');
    setEditDescription(setting.description ?? '');
    setAlert({ show: false, type: '', message: '' });
  };

  const handleEditCancel = () => {
    setEditingKey(null);
    setEditValue('');
    setEditDescription('');
  };

  const handleEditSave = async (key) => {
    setSaving(true);
    try {
      const result = await settingsService.updateSetting(
        key,
        editValue,
        editDescription
      );

      if (result.success) {
        setAlert({
          show: true,
          type: 'success',
          message: `Parametro '${key}' aggiornato con successo.`
        });
        setEditingKey(null);
        await loadSettings();
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: result.message || 'Errore nell\'aggiornamento del parametro.'
        });
      }
    } catch (err) {
      console.error('Error updating setting:', err);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore di connessione durante l\'aggiornamento.'
      });
    } finally {
      setSaving(false);
    }
  };

  const renderValueCell = (setting) => {
    const val = setting.value ?? '';
    const isExpanded = expandedKeys.has(setting.key);
    const isLong = val.length > TRUNCATE_THRESHOLD || val.includes('\n');

    const displayVal = isLong && !isExpanded ? `${val.slice(0, TRUNCATE_THRESHOLD)}...` : val;

    return (
      <div>
        <div
          style={{
            fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: '0.85rem',
            wordBreak: 'break-all',
            whiteSpace: isExpanded ? 'pre-wrap' : 'normal',
            backgroundColor: '#f8f9fa',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #e9ecef',
            maxHeight: isExpanded ? '300px' : 'none',
            overflowY: isExpanded ? 'auto' : 'visible'
          }}
        >
          {displayVal || <span className="text-muted fst-italic">(vuoto)</span>}
        </div>
        {isLong && (
          <Button
            variant="link"
            size="sm"
            className="p-0 mt-1 text-decoration-none"
            style={{ fontSize: '0.78rem' }}
            onClick={() => toggleExpand(setting.key)}
          >
            {isExpanded ? (
              <>
                <i className="fas fa-chevron-up me-1"></i>Riduci
              </>
            ) : (
              <>
                <i className="fas fa-chevron-down me-1"></i>Mostra tutto ({val.length} car.)
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  if (!isCurrentUserAdmin) {
    return (
      <Container className="py-4">
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Questa sezione è riservata agli amministratori.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div>
          <h2 className="mb-1">
            <i className="fas fa-sliders-h me-2 text-primary"></i>
            Parametri di Sistema
          </h2>
          <p className="text-muted mb-0">
            Gestione centralizzata dei parametri applicativi e delle configurazioni di sistema.
          </p>
        </div>
        <div>
          <Button
            variant="primary"
            onClick={handleOpenNewRow}
            disabled={isCreating || saving}
            className="d-flex align-items-center gap-2"
          >
            <i className="fas fa-plus"></i>
            Nuovo Parametro
          </Button>
        </div>
      </div>

      {alert.show && (
        <Alert
          variant={alert.type}
          dismissible
          onClose={() => setAlert({ show: false, type: '', message: '' })}
          className="mb-4"
        >
          {alert.message}
        </Alert>
      )}

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-center">
            <Col md={6} lg={4}>
              <InputGroup>
                <InputGroup.Text className="bg-white border-end-0">
                  <i className="fas fa-search text-muted"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Cerca per chiave, valore o descrizione..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-start-0 ps-0"
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                    title="Cancella ricerca"
                    className="border-start-0"
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col className="text-md-end text-muted small">
              Totale parametri: <Badge bg="secondary">{filteredSettings.length}</Badge>
              {searchTerm && ` (filtrati da ${settings.length})`}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '22%' }}>Chiave</th>
                  <th style={{ width: '33%' }}>Valore</th>
                  <th style={{ width: '25%' }}>Descrizione</th>
                  <th style={{ width: '10%' }}>Aggiornato</th>
                  <th style={{ width: '10%', minWidth: '100px' }} className="text-end">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Insertion Row */}
                {isCreating && (
                  <tr className="table-primary-subtle bg-light border-bottom border-primary">
                    <td className="align-top">
                      <Form.Group>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="es. Sezione.Chiave"
                          value={newKey}
                          onChange={(e) => {
                            setNewKey(e.target.value);
                            if (newKeyError) setNewKeyError('');
                          }}
                          isInvalid={!!newKeyError}
                          maxLength={MAX_KEY_LENGTH}
                          disabled={saving}
                          autoFocus
                        />
                        <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                          {newKeyError}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                          Lettere, numeri, punti, trattini e underscore.
                        </Form.Text>
                      </Form.Group>
                    </td>
                    <td className="align-top">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        style={{
                          fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          fontSize: '0.85rem'
                        }}
                        placeholder="Valore del parametro..."
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        maxLength={MAX_VALUE_LENGTH}
                        disabled={saving}
                      />
                      <div className="text-end text-muted" style={{ fontSize: '0.7rem' }}>
                        {newValue.length}/{MAX_VALUE_LENGTH}
                      </div>
                    </td>
                    <td className="align-top">
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Descrizione opzionale..."
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                        maxLength={MAX_DESC_LENGTH}
                        disabled={saving}
                      />
                      <div className="text-end text-muted" style={{ fontSize: '0.7rem' }}>
                        {newDescription.length}/{MAX_DESC_LENGTH}
                      </div>
                    </td>
                    <td className="align-top text-muted small fst-italic">
                      Nuovo
                    </td>
                    <td className="align-top text-end">
                      <div className="d-flex justify-content-end gap-1">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={handleCreateSave}
                          disabled={saving}
                          title="Salva nuovo parametro"
                        >
                          {saving ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <i className="fas fa-check"></i>
                          )}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleCreateCancel}
                          disabled={saving}
                          title="Annulla inserimento"
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Loading state */}
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      <Spinner animation="border" variant="primary" className="me-2" />
                      Caricamento parametri in corso...
                    </td>
                  </tr>
                ) : filteredSettings.length === 0 && !isCreating ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      <i className="fas fa-sliders-h fa-2x mb-3 d-block text-secondary opacity-50"></i>
                      {searchTerm ? 'Nessun parametro corrisponde ai criteri di ricerca' : 'Nessun parametro di sistema configurato'}
                    </td>
                  </tr>
                ) : (
                  filteredSettings.map((setting) => {
                    const isEditingThis = editingKey === setting.key;

                    if (isEditingThis) {
                      return (
                        <tr key={setting.key} className="table-warning-subtle bg-light">
                          <td className="align-top">
                            <span
                              className="badge bg-secondary-subtle text-dark border font-monospace"
                              style={{ fontSize: '0.85rem' }}
                            >
                              {setting.key}
                            </span>
                          </td>
                          <td className="align-top">
                            <Form.Control
                              as="textarea"
                              rows={2}
                              size="sm"
                              style={{
                                fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                fontSize: '0.85rem'
                              }}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              maxLength={MAX_VALUE_LENGTH}
                              disabled={saving}
                              autoFocus
                            />
                            <div className="text-end text-muted" style={{ fontSize: '0.7rem' }}>
                              {editValue.length}/{MAX_VALUE_LENGTH}
                            </div>
                          </td>
                          <td className="align-top">
                            <Form.Control
                              type="text"
                              size="sm"
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              maxLength={MAX_DESC_LENGTH}
                              disabled={saving}
                            />
                            <div className="text-end text-muted" style={{ fontSize: '0.7rem' }}>
                              {editDescription.length}/{MAX_DESC_LENGTH}
                            </div>
                          </td>
                          <td className="align-top text-muted small">
                            {setting.updatedAt
                              ? moment(setting.updatedAt).format('DD/MM/YYYY HH:mm')
                              : '—'}
                          </td>
                          <td className="align-top text-end">
                            <div className="d-flex justify-content-end gap-1">
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleEditSave(setting.key)}
                                disabled={saving}
                                title="Salva modifiche"
                              >
                                {saving ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <i className="fas fa-check"></i>
                                )}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleEditCancel}
                                disabled={saving}
                                title="Annulla modifiche"
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={setting.key}>
                        <td>
                          <span
                            className="font-monospace text-primary fw-medium user-select-all"
                            style={{ fontSize: '0.85rem' }}
                          >
                            {setting.key}
                          </span>
                        </td>
                        <td>{renderValueCell(setting)}</td>
                        <td className="text-secondary small">
                          {setting.description || <span className="text-muted fst-italic">—</span>}
                        </td>
                        <td className="text-muted small">
                          {setting.updatedAt
                            ? moment(setting.updatedAt).format('DD/MM/YYYY HH:mm')
                            : '—'}
                        </td>
                        <td className="text-end">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleStartEdit(setting)}
                            disabled={isCreating || editingKey !== null}
                            className="d-inline-flex align-items-center gap-1"
                            title="Modifica parametro"
                          >
                            <i className="fas fa-pencil-alt"></i>
                            <span className="d-none d-md-inline">Modifica</span>
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AppSettings;
